// Libraries
import { FormControl, FormLabel, InputGroup, NumberInput, NumberInputField, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
// Utils
import { parseToNumber, parseToThousands } from '@helpers/index';
// Types
import { DebouncedNumericInputProps } from './DebouncedNumericInput.types';

/**
 * A debounced numeric input component.
 *
 * @components
 * @param {string} label - The label for the input.
 * @param {number} min - The minimum value allowed.
 * @param {number} max - The maximum value allowed.
 * @param {number} step - The step value for incrementing/decrementing the input.
 * @param {number} debounce - The debounce time in milliseconds.
 * @param {number} defaultValue - The default value for the input.
 * @param {number} value - The current value for the input.
 * @param {Function} onChange - The callback function called when the value changes.
 * @returns {ReactElement} The debounced numeric input component.
 */
export const DebouncedNumericInput = ({
  label,
  min = 0,
  max,
  step = 1,
  debounce = 500,
  defaultValue = 0,
  value: initialValue,
  error = '',
  onChange,
}: DebouncedNumericInputProps) => {
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    setValue(initialValue as number);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
    // NOTE: The dep onChange is disabled the caller creates a nw instance in each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounce, value]);

  return (
    <FormControl>
      <FormLabel fontSize='s' color='grey.100' mb={1}>
        {label}
      </FormLabel>
      <InputGroup>
        <NumberInput
          borderColor='grey.20'
          allowMouseWheel
          isInvalid={!!error}
          errorBorderColor='text.error'
          w='full'
          step={step}
          min={min}
          max={max}
          onChange={(valueString) => setValue(parseToNumber(valueString))}
          value={parseToThousands(value)}
        >
          <NumberInputField />
        </NumberInput>
      </InputGroup>
      {!!error && (
        <Text h={3.5} mt={1} color='red'>
          {error}
        </Text>
      )}
    </FormControl>
  );
};
